<template lang="html">
  <!--
  Component to render <input> element
  - Accepts the following properties:
  [id, type, name, placeholder, required, value]

  - Accepts optional callback property:
  [on-text-input-change, on-text-input-focus, on-text-input-blur]

  @example

  <TextInput
    ref="test1"
    id="pwd"
    type="password"
    placeholder="Enter password"
    required="true"
    @on-text-input-focus="onTextInpuFocus"
    @on-text-input-blur="onTextInputBlur"
    @on-text-input-change="onTextInputChanged"/>

  -->

  <section class="m-text-input__container">
    <div class="m-text-input">
      <label class="m-text-input__label" v-if="label">{{ label }}</label>
      <input
        ref="inputElement"
        class="m-input"
        :class="{ 'm-input-right-icon': hasRightIcon }"
        v-model="model"
        :id="id"
        :type="type"
        :name="name"
        :min="min"
        :placeholder="placeholder"
        :required="required"
        @keydown.enter.stop="onPressEnter"
        @keydown.up.stop="onPressUp"
        @keydown.down.stop="onPressDown"
        @keydown.tab.stop="onPressTab"
        autocomplete="off"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @click="onInputFocus"
      />
    </div>
  </section>
</template>

<script lang="js">

  export default {
    name: "text-input",
    props: {
      /**
       * input properties
       * @values id, type, name, placeholder, value, required
       */
      id: {
        type: String
      },
      type: {
        type: String
      },
      name: {
        type: String
      },
      min: {
        type: String
      },
      placeholder: {
        type: String
      },
      value: {
        type: String
      },
      required: {
        type: Boolean
      },
      label: {
        type: String
      },
      select: {
        type: Boolean
      },
      focus: {
        type: Boolean
      },
      isMultiSelect: {
        type: Boolean
      },
      hasRightIcon: {
        type: Boolean
      }
    },
    mounted() {
      if (this.focus) {
        this.$refs.inputElement.focus();
      }
    },
    data() {
      return {
        model: ""
      }
    },
    watch: {
      value: {
        handler() {
          this.model = this.value;
        },
        immediate: true
      },
      model: function () {
        /**
         * Triggers when model value changes
         * @event on-text-input-change
         */
        if(this.$listeners && this.$listeners["on-text-input-change"]) {
          this.$emit("on-text-input-change", this.model, this.id);
        }

      },
      select: function(val) {
        if(val) {
          this.$refs.inputElement.select();
        }
      }
    },
    methods: {
      onPressUp() {
        this.$emit("on-up-arrow-pressed", this.model, this.id);
      },
      onPressDown() {
        this.$emit("on-down-arrow-pressed", this.model, this.id);
      },
      onPressTab() {
        this.$emit("on-tab-pressed");
      },
      onPressEnter() {
        if (this.isMultiSelect) {
          this.$emit("on-multi-select-press-enter", this.model, this.id);
        } else {
          this.$emit("on-single-select-press-enter", this.model, this.id);
        }
      },
      onInputFocus() {
        /**
         * Triggers focus input
         * @event on-text-input-focus
         */
        if(this.$listeners && this.$listeners["on-text-input-focus"]) {
          this.$emit("on-text-input-focus", this.model, this.id);
        }
      },
      onInputBlur() {
        /**
         * Triggers blur input
         * @event on-text-input-blur
         */
        if(this.$listeners && this.$listeners["on-text-input-blur"]) {
          this.$emit("on-text-input-blur", this.model, this.id);
        }
      }
    }
  };
</script>

<style scoped lang="scss">
  @import "../styles/components/elements";

  .m-text-input {
    display: flex;
    flex-direction: column;
  }
  .m-input {
    @include mixin-text-input;
  }
  .m-text-input__label {
    @include mixin-label;
  }

  .m-input-right-icon {
    padding: 0 2.25rem 0 0.75rem;
  }
</style>
